import reactUpdate from 'immutability-helper';
import {useMemo, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';

import {
  buildReactionFromComponents,
  BuildReactionFromComponentsResponse,
  ReactionComponentInput,
} from 'chemistry/ChemistryDAO';
import makeCancelable from 'lib/makeCancelable';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  getColumnIndices,
  getMoleculeIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
  SyncedReactionComponents,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {NoteLinkedFile, FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';

function serverNoteLinkedFileToClientNoteLinkedFile(
  serverNoteLinkedFile: FromServerNoteLinkedFile
): NoteLinkedFile {
  // Use the server-assigned id as the client cid so the patcher can find the
  // existing NoteLinkedFile on save without insecure creation via the patcher path.
  const {id, ...rest} = serverNoteLinkedFile;
  return {...rest, cid: id};
}

// Backward compatibility: older persisted tables store syncedComponents as plain molecule ID
// strings. Normalize to tagged union format so isEqual comparisons work correctly.
function normalizeSyncedComponents(
  components: SyncedReactionComponents | undefined
): SyncedReactionComponents | undefined {
  if (components == null) {
    return undefined;
  }
  return {
    [ChemistryReactionRole.REACTANT]: components[ChemistryReactionRole.REACTANT].map((c) =>
      typeof c === 'string' ? {type: 'molecule' as const, id: c} : c
    ),
    [ChemistryReactionRole.PRODUCT]: components[ChemistryReactionRole.PRODUCT].map((c) =>
      typeof c === 'string' ? {type: 'molecule' as const, id: c} : c
    ),
  };
}

function extractCurrentTableComponents(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>,
  rows: Array<StoichiometryTableRow>
): SyncedReactionComponents {
  const {compoundColumnIndex, roleColumnIndex} = getColumnIndices(columns);
  const components: SyncedReactionComponents = {
    [ChemistryReactionRole.REACTANT]: [],
    [ChemistryReactionRole.PRODUCT]: [],
  };

  for (let i = 0; i < computedTable.length; i++) {
    const evaluatedRow = computedTable[i];
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);

    if (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT) {
      if (moleculeId) {
        components[role].push({type: 'molecule', id: moleculeId});
      } else {
        const smiles = rows[i]?.metadata?.canonicalSmiles;
        if (smiles) {
          components[role].push({type: 'smiles', smiles});
        }
      }
    }
  }

  return components;
}

function rowToApiComponent(
  row: StoichiometryTableRow | undefined,
  evaluatedRow: EvaluatedTable[number],
  compoundColumnIndex: number
): ReactionComponentInput | null {
  if (row?.metadata?.drawnMolfile) {
    return {type: 'molfile', molfile: row.metadata.drawnMolfile};
  }
  const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
  if (moleculeId) {
    return {type: 'molecule', moleculeId};
  }
  const smiles = row?.metadata?.canonicalSmiles;
  if (smiles) {
    return {type: 'smiles', smiles};
  }
  return null;
}

function buildApiComponentsFromRows(
  computedTable: EvaluatedTable,
  columns: Array<StructuredTableColumn>,
  rows: Array<StoichiometryTableRow>
): {reactants: Array<ReactionComponentInput>; products: Array<ReactionComponentInput>} {
  const {compoundColumnIndex, roleColumnIndex} = getColumnIndices(columns);
  const reactants: Array<ReactionComponentInput> = [];
  const products: Array<ReactionComponentInput> = [];

  for (let i = 0; i < computedTable.length; i++) {
    const evaluatedRow = computedTable[i];
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);
    if (role === ChemistryReactionRole.REACTANT) {
      const apiComponent = rowToApiComponent(rows[i], evaluatedRow, compoundColumnIndex);
      if (apiComponent != null) {
        reactants.push(apiComponent);
      }
    } else if (role === ChemistryReactionRole.PRODUCT) {
      const apiComponent = rowToApiComponent(rows[i], evaluatedRow, compoundColumnIndex);
      if (apiComponent != null) {
        products.push(apiComponent);
      }
    }
  }

  return {reactants, products};
}

interface PrepareAndBuildResult {
  response: BuildReactionFromComponentsResponse;
  components: SyncedReactionComponents;
}

async function prepareAndBuildUpdatedReactionDiagram(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable
): Promise<PrepareAndBuildResult> {
  // syncedComponents tracks component identity (molecule ID or SMILES) for staleness detection.
  // buildApiComponentsFromRows separately prefers drawnMolfile in the API payload to preserve
  // user-drawn 2D orientation without resolving entity molfiles on the frontend.
  const components = extractCurrentTableComponents(
    computedTable,
    structuredTable.columns,
    structuredTable.rows
  );
  const {reactants, products} = buildApiComponentsFromRows(
    computedTable,
    structuredTable.columns,
    structuredTable.rows
  );
  const response = await buildReactionFromComponents(reactants, products);
  return {response, components};
}

/**
 * Syncs reaction components from a stoichiometry table to a reaction scheme file.
 *
 * Monitors reactant and product molecules in the table and automatically updates
 * the reaction scheme when components change. Calls the chemistry service to build
 * a new reaction diagram and invokes `onUpdate` with both the updated NoteLinkedFile
 * (carrying the new external file) and the updated table (with refreshed syncedComponents).
 *
 * @param structuredTable - The stoichiometry table containing reaction components
 * @param computedTable - The evaluated (computed) version of the table rows
 * @param reactionSchemeFile - The NoteLinkedFile that stores the reaction scheme drawing
 * @param onUpdate - Callback invoked with (updatedFile, updatedTable) when a new diagram is generated
 * @returns true if a diagram update is in progress, false otherwise
 */
export default function useSyncReactionComponentsFromTable(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  reactionSchemeFile: NoteLinkedFile,
  onUpdate: (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void
): boolean {
  const diagramReactionComponents = useMemo(
    () => normalizeSyncedComponents(structuredTable.metadata.syncedComponents),
    [structuredTable.metadata.syncedComponents]
  );
  const tableReactionComponents = useMemo(
    () => extractCurrentTableComponents(computedTable, structuredTable.columns, structuredTable.rows),
    [computedTable, structuredTable.columns, structuredTable.rows]
  );
  // Count in-flight builds rather than a single boolean so overlapping runs (a build canceled
  // on cleanup that resolves after its replacement started) can't clobber each other's spinner state.
  const [inFlightCount, setInFlightCount] = useState(0);

  useEffect(() => {
    if (
      diagramReactionComponents != null &&
      // Use deep equality check since the tableReactionComponents array is not completely stable.
      isEqual(tableReactionComponents, diagramReactionComponents)
    ) {
      return;
    }

    // Tie the result to the run that started it. If the table state changes while the build is
    // in flight (e.g. the sketcher is submitted), canceling on cleanup discards this run's result
    // rather than reverting the sketcher's file.
    const {promise, cancel} = makeCancelable(
      prepareAndBuildUpdatedReactionDiagram(structuredTable, computedTable)
    );
    setInFlightCount((count) => count + 1);

    (async (): Promise<void> => {
      try {
        const {response, components} = await promise;
        const {externalFile, noteLinkedFile} = response;
        if (
          reactionSchemeFile.externalFile != null &&
          externalFile.id === reactionSchemeFile.externalFile.id
        ) {
          return;
        }
        onUpdate(
          serverNoteLinkedFileToClientNoteLinkedFile(noteLinkedFile),
          reactUpdate(structuredTable, {
            metadata: {$merge: {syncedComponents: components}},
          })
        );
      } catch (err) {
        // A run superseded via cleanup rejects with isCanceled; that's expected. Let any real
        // failure surface instead of silently leaving the diagram out of sync.
        if (!err?.isCanceled) {
          throw err;
        }
      } finally {
        setInFlightCount((count) => count - 1);
      }
    })();

    return cancel;
  }, [
    tableReactionComponents,
    diagramReactionComponents,
    structuredTable,
    computedTable,
    onUpdate,
    reactionSchemeFile,
  ]);

  return inFlightCount > 0;
}
